import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button, Card, Form, Input, Select, Upload, Typography, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { DocumentStatus, DocumentType } from "~/utils/constants";
import { useCreateFile } from "~/services/file";

const { Title, Text } = Typography;
const { Option } = Select;

interface CreateDocumentFormValues {
  title: string;
  type: string;
  status: string;
  clientId?: string;
  serviceTicketId?: string;
}

export const CreateDocument: React.FC = () => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<any[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract document type from URL query parameter
  const queryParams = new URLSearchParams(location.search);
  const docType = queryParams.get("type") || "CONDITION";

  // Use React Query mutation hook
  const createFileMutation = useCreateFile();

  const handleUploadChange = (info: any) => {
    let fileList = [...info.fileList];
    // Limit to only one file
    fileList = fileList.slice(-1);
    setFileList(fileList);
  };

  const handleSubmit = async (values: CreateDocumentFormValues) => {
    if (fileList.length === 0) {
      message.error("Please upload a document file");
      return;
    }

    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("type", values.type);
    formData.append("status", values.status || DocumentStatus.SENT);
    
    if (values.clientId) {
      formData.append("clientId", values.clientId);
    }
    
    if (values.serviceTicketId) {
      formData.append("serviceTicketId", values.serviceTicketId);
    }
    
    formData.append("file", fileList[0].originFileObj);

    try {
      await createFileMutation.mutateAsync(formData);
      message.success("Document created successfully");
      navigate("/documents");
    } catch (error) {
      console.error("Error creating document:", error);
      message.error("Failed to create document");
    }
  };

  React.useEffect(() => {
    form.setFieldsValue({
      type: docType,
      status: DocumentStatus.SENT,
    });
  }, [docType, form]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <Title level={3}>Create Document</Title>
        <Text type="secondary">
          {docType === "CONDITION" 
            ? "Upload a condition report for artwork" 
            : "Create a delivery waybill for shipment"}
        </Text>
      </div>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            type: docType,
            status: DocumentStatus.SENT,
          }}
        >
          <Form.Item
            name="title"
            label="Document Title"
            rules={[{ required: true, message: "Please enter document title" }]}
          >
            <Input placeholder="Enter document title" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Document Type"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value={DocumentType.CONDITION}>Condition Report</Option>
              <Option value={DocumentType.WAYBILL}>Delivery Waybill</Option>
              <Option value={DocumentType.INVOICE}>Invoice</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="Document Status"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value={DocumentStatus.SENT}>Sent</Option>
              <Option value={DocumentStatus.SIGNED}>Signed</Option>
              <Option value={DocumentStatus.COMPLETE}>Complete</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="clientId"
            label="Client (Optional)"
          >
            <Select
              placeholder="Select client"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {/* This would be populated with client data from API */}
              <Option value="placeholder">Sample Client</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="serviceTicketId"
            label="Service Ticket (Optional)"
          >
            <Select
              placeholder="Select service ticket"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {/* This would be populated with service ticket data from API */}
              <Option value="placeholder">Sample Ticket</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Document File"
            rules={[{ required: true, message: "Please upload a document file" }]}
          >
            <Upload
              beforeUpload={() => false}
              onChange={handleUploadChange}
              fileList={fileList}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
            <Text type="secondary" className="mt-2 block">
              Supported formats: PDF, DOCX, JPG, PNG (Max: 10MB)
            </Text>
          </Form.Item>

          <Form.Item>
            <div className="flex justify-end space-x-2">
              <Button onClick={() => navigate("/documents")}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={createFileMutation.isPending}
              >
                Create Document
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};